import { z } from 'zod';

/** What a character's attributes give (CHAR-LEVEL): computed by the server, shown by clients. */
export const CharacterSheetSchema = z.object({
  maxHealthFactor: z.number().positive(),
  meleeDamageFactor: z.number().positive(),
  /** × each technique's recovery, from the Control (R-07). */
  techniqueRecoveryFactor: z.number().positive(),
  carry: z.number().int().nonnegative(),
  /** × the materials of crafting by a craft's mastery, and of building (CHAR-MASTERY). */
  craftCostFactors: z.record(z.string(), z.number().positive()),
  buildCostFactor: z.number().positive(),
});
export type CharacterSheetView = z.infer<typeof CharacterSheetSchema>;

/** A character's level, experience and attributes (CHAR-LEVEL, UX-CHAR). */
export const ProgressSchema = z.object({
  level: z.number().int().positive(),
  maxLevel: z.number().int().positive(),
  /** Experience into the current level, and what the next one needs (0 at the maximum). */
  intoLevel: z.number().int().nonnegative(),
  toNext: z.number().int().nonnegative(),
  /** Points earned and not yet put in an attribute. */
  unspent: z.number().int().nonnegative(),
  /** The most points one attribute takes (the sheet draws each attribute's track to it). */
  maxPerAttribute: z.number().int().positive().optional(),
  /**
   * Every attribute, in the order the rules list them. `open`: points can go into it. `benefits`:
   * what it gives now (`value`), with one point more (`next`) and at most (`cap`), as fractions
   * (0.05: +5 %) or units (carry, Focus, body), computed by the rules (UX-CHAR).
   */
  attributes: z.array(
    z.object({
      id: z.string(),
      points: z.number().int().nonnegative(),
      open: z.boolean(),
      benefits: z.array(
        z.object({
          benefit: z.string(),
          value: z.number().nonnegative(),
          next: z.number().nonnegative(),
          cap: z.number().positive(),
          /** Points more until it gives more (a band's step, AO-7); null at its cap. */
          toNext: z.number().int().positive().nullable().optional(),
        }),
      ),
      /**
       * Its perks by threshold (CHAR-ATTRIBUTE §6, AO-3): the ids offered there (names by i18n,
       * `perks.<id>`) and the one chosen, if any.
       */
      perks: z.array(
        z.object({
          threshold: z.number().int().positive(),
          offered: z.array(z.string()),
          chosen: z.string().nullable(),
        }),
      ),
    }),
  ),
  /**
   * The hybrids (CHAR-ATTRIBUTE AO-4): each pair of attributes, the points both need, and whether
   * it is held now (never chosen). Names by i18n, `perks.<id>`.
   */
  hybrids: z.array(
    z.object({
      id: z.string(),
      of: z.tuple([z.string(), z.string()]),
      at: z.number().int().positive(),
      held: z.boolean(),
    }),
  ),
  /** Every mastery, in the order the rules list them (CHAR-MASTERY). */
  masteries: z.array(
    z.object({
      id: z.string(),
      rank: z.number().int().nonnegative(),
      practice: z.number().int().nonnegative(),
      /** Practices the next rank needs in total (0 at the highest). */
      nextAt: z.number().int().nonnegative(),
    }),
  ),
  sheet: CharacterSheetSchema,
});
export type Progress = z.infer<typeof ProgressSchema>;
